import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AlertOctagon,
  AlertTriangle,
  Bell,
  Check,
  CheckCheck,
  Filter,
  Inbox,
  Info,
  Search,
} from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { cn } from '../lib/cn';
import type {
  AppNotification,
  NotificationCategory,
  NotificationSeverity,
} from '../mocks/clientBatch';
import { useMyNotifications } from '../api/platform';

const CATEGORY_LABELS: Record<NotificationCategory | 'all', string> = {
  all: 'All',
  billing: 'Billing',
  security: 'Security',
  system: 'System',
  team: 'Team',
  commission: 'Commission',
  application: 'Application',
};

const SEVERITY_DOT: Record<NotificationSeverity, string> = {
  info: 'bg-sky-500 dark:bg-sky-400',
  warning: 'bg-amber-500 dark:bg-amber-400',
  critical: 'bg-rose-500 dark:bg-rose-400',
};

const SEVERITY_ICON: Record<NotificationSeverity, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertOctagon,
};

const SEVERITY_TEXT: Record<NotificationSeverity, string> = {
  info: 'text-sky-700 dark:text-sky-300',
  warning: 'text-amber-700 dark:text-amber-300',
  critical: 'text-rose-700 dark:text-rose-300',
};

/** Full notifications history page — reachable from the bell-icon "View all → " link. */
export function Notifications() {
  const { data: myNotifications } = useMyNotifications();
  const [filter, setFilter] = useState<NotificationCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const [readSet, setReadSet] = useState<Set<string>>(new Set());

  // Seed the read-set once notifications load (those already read server-side).
  // Keyed on the raw query data (stable until loaded) so it runs once.
  useEffect(() => {
    if (!myNotifications) return;
    setReadSet(new Set(myNotifications.filter((n) => n.readAt !== null).map((n) => n.id)));
  }, [myNotifications]);

  function isRead(n: AppNotification) {
    return readSet.has(n.id);
  }
  function markRead(id: string) {
    setReadSet((prev) => new Set(prev).add(id));
  }
  function markAllRead() {
    setReadSet(new Set((myNotifications ?? []).map((n) => n.id)));
  }

  const filtered = useMemo(() => {
    return (myNotifications ?? []).filter((n) => {
      if (filter !== 'all' && n.category !== filter) return false;
      if (search && !`${n.title} ${n.body}`.toLowerCase().includes(search.toLowerCase()))
        return false;
      return true;
    });
  }, [myNotifications, filter, search]);

  const unreadCount = (myNotifications ?? []).filter((n) => !isRead(n)).length;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Inbox"
        title={
          <span className="flex items-center gap-2">
            Notifications
            {unreadCount > 0 && (
              <span className="px-1.5 py-px rounded text-[10px] font-medium bg-brand text-white dark:bg-brand-dm dark:text-zinc-950 align-middle">
                {unreadCount} unread
              </span>
            )}
          </span>
        }
        subtitle="Everything that's happened in your account."
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={markAllRead}
            disabled={unreadCount === 0}
            iconLeft={<CheckCheck className="w-4 h-4" />}
          >
            Mark all read
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 border-b border-zinc-200 dark:border-zinc-700 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400 mr-0.5" />
            {(Object.keys(CATEGORY_LABELS) as Array<keyof typeof CATEGORY_LABELS>).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-medium transition',
                  filter === k
                    ? 'bg-zinc-900 text-white dark:bg-zinc-50 dark:text-zinc-950'
                    : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800',
                )}
              >
                {CATEGORY_LABELS[k]}
              </button>
            ))}
          </div>
          <Input
            prefix={<Search className="w-4 h-4" />}
            placeholder="Search notifications…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={<Inbox className="w-8 h-8" />}
              title="Nothing here"
              description={search ? 'No notifications match your search.' : "You're all caught up."}
            />
          </div>
        ) : (
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {filtered.map((n) => {
              const SevIcon = SEVERITY_ICON[n.severity];
              const read = isRead(n);
              return (
                <li
                  key={n.id}
                  className={cn(
                    'group relative px-5 sm:px-6 py-4 transition',
                    !read && 'bg-zinc-50/60 dark:bg-zinc-800/40',
                    'hover:bg-zinc-100/60 dark:hover:bg-zinc-800/70',
                  )}
                >
                  {!read && (
                    <span
                      className="absolute left-0 top-0 bottom-0 w-1 bg-brand dark:bg-brand-dm"
                      aria-hidden="true"
                    />
                  )}
                  <div className="flex items-start gap-3">
                    <span className={cn('mt-0.5 flex-shrink-0', SEVERITY_TEXT[n.severity])}>
                      <SevIcon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span
                          className={cn(
                            'font-medium text-zinc-950 dark:text-zinc-50',
                            !read && 'font-semibold',
                          )}
                        >
                          {n.title}
                        </span>
                        <span className="text-[11px] uppercase tracking-wider text-zinc-400 dark:text-zinc-500 font-medium">
                          {CATEGORY_LABELS[n.category]}
                        </span>
                      </div>
                      <p className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-300">{n.body}</p>
                      <div className="mt-2 flex items-center gap-3 text-xs text-zinc-500 dark:text-zinc-400 flex-wrap">
                        <span>{n.createdAt}</span>
                        {n.actionUrl && (
                          <Link
                            to={n.actionUrl as never}
                            onClick={() => markRead(n.id)}
                            className="font-medium text-brand dark:text-brand-dm hover:underline"
                          >
                            {n.actionLabel ?? 'Open →'}
                          </Link>
                        )}
                        {!read && (
                          <button
                            type="button"
                            onClick={() => markRead(n.id)}
                            className="font-medium text-zinc-700 dark:text-zinc-200 hover:underline inline-flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" />
                            Mark read
                          </button>
                        )}
                      </div>
                    </div>
                    <span
                      className={cn(
                        'mt-1 w-2 h-2 rounded-full flex-shrink-0',
                        read ? 'bg-transparent' : SEVERITY_DOT[n.severity],
                      )}
                      aria-label={read ? 'read' : 'unread'}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
          <Bell className="w-3.5 h-3.5" />
          <span>
            Manage what kinds of notifications you receive in{' '}
            <Link
              to="/profile"
              className="font-medium text-brand dark:text-brand-dm hover:underline"
            >
              Profile → Notifications
            </Link>
            .
          </span>
        </div>
      </Card>
    </div>
  );
}
